import React, { useState, useEffect, useCallback, useRef } from 'react';

interface LED {
  id: number;
  isOn: boolean;
  isEmphasized: boolean;
  color?: string;
}

const DotPanel: React.FC = () => {
  const [leds, setLeds] = useState<LED[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const generateLEDs = useCallback(() => {
    const newLeds: LED[] = [];
    const totalLeds = 126; // 6x21 grid

    const orangeColors = [
      '#ff6b35',
      '#ff8c42',
      '#ffa366',
      '#ffb366',
      '#ffcc99',
      '#ffd699',
    ];

    for (let i = 0; i < totalLeds; i++) {
      const isOn = Math.random() < 0.15;
      const isEmphasized = isOn && Math.random() < 0.6;
      
      const randomColor = orangeColors[Math.floor(Math.random() * orangeColors.length)];

      newLeds.push({
        id: i,
        isOn,
        isEmphasized,
        color: isOn ? randomColor : undefined
      });
    }
    setLeds(newLeds);
  }, []);

  const startRandomChanges = useCallback(() => {
    const id = setInterval(generateLEDs, 3000);
    intervalRef.current = id;
  }, [generateLEDs]);

  const stopRandomChanges = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const generateRixinoPattern = useCallback(() => {
    const newLeds: LED[] = [];
    const totalLeds = 126;

    const rixinoPattern = [
      [
        4,25,46,67,88,109,      
        5,6,7,                  
        29,50,                  
        47,48,49,               
        69,91,113               
      ],
      [
        11,33,55,77,99,121,       
        16,36,56,76,96,116        
      ]
    ];

    const allPositions = rixinoPattern.flat();

    for (let i = 0; i < totalLeds; i++) {
      const isInPattern = allPositions.includes(i);

      newLeds.push({
        id: i,
        isOn: isInPattern,
        isEmphasized: isInPattern,
        color: isInPattern ? '#ff6b35' : undefined
      });
    }
    setLeds(newLeds);
  }, []);

  const handleMouseEnter = useCallback(() => {
    stopRandomChanges();
    generateRixinoPattern();
  }, [stopRandomChanges, generateRixinoPattern]);

  const handleMouseLeave = useCallback(() => {
    startRandomChanges();
  }, [startRandomChanges]);

  useEffect(() => {
    generateLEDs();
    startRandomChanges();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [generateLEDs, startRandomChanges]);

  return (
    <div
      className="dot-panel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="led-panel">
        {leds.map((led) => (
          <div
            key={led.id}
            data-key={led.id}
            className={`led ${led.isOn ? 'is-on' : 'is-off'} ${led.isEmphasized ? 'is-emphasized' : ''}`}
            style={{
              backgroundColor: led.color || undefined,
              boxShadow: led.color ? `0 0 3px ${led.color}, 0 0 6px ${led.color}, 0 0 9px ${led.color}` : undefined
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DotPanel;
